import { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Heading,
  Icon,
  LoadingIcon,
  Stack,
  Text,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useTenantIdFromParams } from '@eluve/session-helpers';

import { CreateTemplateForm } from './CreateTemplateDialog';
import { DeleteTemplateAlert } from './DeleteTemplateAlert';
import { PdfTemplateCreateFlow } from './PdfTemplateCreateFlow';
import { TemplateSection } from './TemplateSection';
import { useDocumentTemplates } from './useDocumentTemplates';
import { usePdfTemplateOperations } from './usePdfTemplateOperations';
import { useTemplateListState } from './useTemplateListState';

export type FillPdfDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isGenerating: boolean;
  onFillPdfForm: (templateId: string, templateName: string) => void;
  templates: ReturnType<typeof useDocumentTemplates>;
};

export const FillPdfDialog: React.FC<FillPdfDialogProps> = ({
  open,
  onOpenChange,
  isGenerating,
  onFillPdfForm,
  templates,
}) => {
  const { t } = useTranslation();
  const tenantId = useTenantIdFromParams();
  const pdfOps = usePdfTemplateOperations(tenantId ?? null);
  const {
    createdByYou,
    createdByEluve,
    loading,
    hasData,
    refetch,
    deleteTemplate,
  } = templates;

  const {
    editingTemplate,
    setEditingTemplate,
    deletingTemplate,
    setDeletingTemplate,
    isDeleting,
    handleDeleteTemplate,
  } = useTemplateListState({ deleteTemplate, refetch });

  const [view, setView] = useState<'list' | 'create' | 'template'>('list');

  const pdfCreatedByYou = createdByYou.filter((t) => t.kind === 'PDF_FORM');
  const pdfCreatedByEluve = createdByEluve.filter((t) => t.kind === 'PDF_FORM');
  const hasTemplates =
    pdfCreatedByYou.length > 0 || pdfCreatedByEluve.length > 0;

  const backToList = () => {
    setView('list');
    setEditingTemplate(null);
  };

  const onSelect = (t: (typeof createdByYou)[number]) => {
    onFillPdfForm(t.id, t.name);
    onOpenChange(false);
  };

  const onEdit = (t: (typeof createdByYou)[number]) => {
    setEditingTemplate({
      id: t.id,
      name: t.name,
      content: t.content ?? '',
      kind: t.kind,
      sourceFileId: t.sourceFileId,
    });
    setView('template');
  };

  const onDelete = (t: (typeof createdByYou)[number]) =>
    setDeletingTemplate({ id: t.id, name: t.name });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // This dialog stays mounted, so reset the subview on close — otherwise
        // reopening lands on the stale create/edit view instead of the list.
        if (!next) backToList();
        onOpenChange(next);
      }}
    >
      <DialogContent
        size="4xl"
        className="max-h-[85vh] gap-0! overflow-hidden p-0!"
      >
        {view === 'create' ? (
          <>
            <DialogTitle className="sr-only">
              {t('appointmentTools.documents.createTemplateTitle')}
            </DialogTitle>
            <div className="max-h-[85vh] overflow-y-auto p-6">
              <PdfTemplateCreateFlow
                onBack={backToList}
                onClose={async () => {
                  await refetch();
                  backToList();
                }}
                onFillPdfForm={(templateId, name) => {
                  onFillPdfForm(templateId, name);
                  onOpenChange(false);
                }}
                uploadFile={pdfOps.uploadFile}
                generate={pdfOps.generate}
                save={pdfOps.save}
                usePreviewUrl={pdfOps.usePreviewUrl}
              />
            </div>
          </>
        ) : view === 'template' ? (
          <>
            <DialogTitle className="sr-only">
              {t('appointmentTools.documents.editTemplateTitle')}
            </DialogTitle>
            <CreateTemplateForm
              template={editingTemplate}
              mode="pdf_form"
              onBack={backToList}
              onSaved={async () => {
                await refetch();
                backToList();
              }}
            />
          </>
        ) : (
          <Stack className="max-h-[85vh] overflow-hidden" align="stretch">
            <Stack gap={4} className="shrink-0 px-6 pt-6 pb-4">
              <Stack gap={1}>
                <DialogTitle asChild>
                  <Heading size="lg">
                    {t('appointmentTools.documents.fillPdfTitle')}
                  </Heading>
                </DialogTitle>
                <Text size="xs" color="tertiary">
                  {t('appointmentTools.documents.fillPdfSubtitle')}
                </Text>
              </Stack>
              <button
                type="button"
                onClick={() => {
                  setEditingTemplate(null);
                  setView('create');
                }}
                className="flex w-full items-center gap-3 rounded-lg border border-border-indigo-strong bg-bg-indigo-subtle-primary/40 px-4 py-3 text-left transition-colors hover:bg-bg-indigo-subtle-primary/70"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-bg-indigo-strong-primary">
                  <Icon name="Plus" size="auto" className="text-white" />
                </span>
                <Stack gap={0}>
                  <Text size="sm" weight="medium">
                    {t('appointmentTools.documents.createPdfTemplate')}
                  </Text>
                  <Text size="xs" color="tertiary">
                    {t('appointmentTools.documents.createPdfTemplateHint')}
                  </Text>
                </Stack>
              </button>
            </Stack>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-2 pb-6">
              {loading && !hasData ? (
                <Stack justify="center" className="py-4" direction="horizontal">
                  <LoadingIcon size="lg" />
                </Stack>
              ) : (
                <Stack>
                  {!hasTemplates && (
                    <Text size="xs" color="tertiary" className="py-3">
                      {t('appointmentTools.documents.noPdfTemplates')}
                    </Text>
                  )}
                  <TemplateSection
                    title={t('appointmentTools.documents.yourPdfTemplates')}
                    templates={pdfCreatedByYou}
                    isGenerating={isGenerating}
                    onSelect={onSelect}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                  <TemplateSection
                    title={t('appointmentTools.documents.eluveTemplates')}
                    templates={pdfCreatedByEluve}
                    isGenerating={isGenerating}
                    onSelect={onSelect}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                </Stack>
              )}
            </div>

            <DeleteTemplateAlert
              template={deletingTemplate}
              onOpenChange={(isOpen) => {
                if (!isOpen) setDeletingTemplate(null);
              }}
              onConfirm={handleDeleteTemplate}
              isDeleting={isDeleting}
            />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
